import { cloneDeep, isEmpty } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import {
  getTranslatedText,
  ReportQueryBuilderForm,
  Loader,
  SuperPop
} from '@nile/components';
import {
  reactSelectErrorStyles,
  reactSelectInlineError
} from '@nile/banyan/src/Utils/style';
import {
  formatGroupLabel,
  IndicatorSeparator
} from '@nile/banyan/src/Pebbles/select';
import { MODULEMAP } from '@nile/reports/src/modules/charts/constants';
import { DropdownArrow } from '@nile/mangifera/src/Pebbles/select';
import {
  Button,
  Collapsible,
  DisplayText,
  TextField,
  TextStyle
} from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import Select from 'react-select';
import { FormattedMessage } from 'react-intl';
import { MetricConfigWrapper } from './styles';

function MetricConfig(props) {
  const {
    data,
    errors,
    index,
    intl,
    meta,
    context,
    validateChartForm,
    metricAttributes,
    metaLoading,
    allEntities,
    allOperators
  } = props;

  const totalMetrics = context?.value?.metrics?.length;
  const metricFilterCondition = context?.value?.metrics[index]?.filterCondition;

  const [active, setActive] = useState(true);

  const [builderLoading, setBuilderLoading] = useState(metaLoading);

  const [selectedMetricAttribute, setSelectedMetricAttribute] = useState(null);

  const DENOMINATOR_VALUES = [
    {
      value: 'FILTER',
      label: getTranslatedText(
        intl,
        'reports.widget.config.denominator.filter.label'
      ),
      checkDisabledCondition: 'filterCondition'
    },
    {
      value: 'GROUP',
      label: getTranslatedText(
        intl,
        'reports.widget.config.denominator.group.label'
      ),
      checkDisabledCondition: 'groupBy'
    }
  ];

  const DENOMINATOR_LABEL_MAP = {
    FILTER: getTranslatedText(
      intl,
      'reports.widget.config.denominator.filter.label'
    ),
    GROUP: getTranslatedText(
      intl,
      'reports.widget.config.denominator.group.label'
    )
  };

  useEffect(() => {
    if (!context.value.metrics[index].attribute)
      setSelectedMetricAttribute(null);
  }, [context.value.entity]);

  useEffect(() => {
    if (!metaLoading) {
      setBuilderLoading(true);
      setTimeout(() => {
        setBuilderLoading(false);
      }, 500);
    }
  }, [context.value.metrics.length]);

  const [selectedFunction, setSelectedFunction] = useState(
    metricAttributes
      .filter(
        (attr) => attr.value === context.value.metrics[index]?.attribute
      )?.[0]
      ?.metricFunctions?.filter(
        (func) => func === context.value.metrics[index]?.metricFunction
      )
      ?.map((func) => ({ value: func, label: func }))?.[0] || null
  );

  const [selectedUnit, setSelectedUnit] = useState(
    metricAttributes
      .filter(
        (attr) => attr.value === context.value.metrics[index]?.attribute
      )?.[0]
      ?.metricUnits?.filter(
        (func) => func === context.value.metrics[index]?.metricUnit
      )
      ?.map((func) => ({ value: func, label: func }))?.[0] || null
  );

  const [selectedDenominator, setselectedDenominator] = useState(
    context.value.metrics[index]?.denominator
      ? {
          value: context.value.metrics[index]?.denominator,
          label:
            DENOMINATOR_LABEL_MAP?.[
              context.value.metrics[index]?.denominator
            ] || 'null'
        }
      : DENOMINATOR_VALUES[0]
  );

  useEffect(() => {
    setSelectedMetricAttribute(
      metricAttributes.filter(
        (attr) => attr.value === context.value.metrics[index]?.attribute
      )?.[0] || null
    );
    setSelectedFunction(
      metricAttributes
        .filter(
          (attr) => attr.value === context.value.metrics[index]?.attribute
        )?.[0]
        ?.metricFunctions?.filter(
          (func) => func === context.value.metrics[index]?.metricFunction
        )
        ?.map((func) => ({ value: func, label: func }))?.[0] || null
    );
    setSelectedUnit(
      metricAttributes
        .filter(
          (attr) => attr.value === context.value.metrics[index]?.attribute
        )?.[0]
        ?.metricUnits?.filter(
          (func) => func === context.value.metrics[index]?.metricUnit
        )
        ?.map((func) => ({ value: func, label: func }))?.[0] || null
    );
  }, [context.value.metrics, metaLoading]);

  const handleToggle = useCallback(() => setActive((val) => !val), []);

  const handleChange = (prop, val) => {
    const updatedMetrics = cloneDeep(context.value.metrics);
    updatedMetrics[index] = { ...updatedMetrics[index], [prop]: val };
    context.updateContext({ ...context.value, metrics: updatedMetrics });
  };

  const updateAutomationRule = (conditions, key) => {
    const updatedMetrics = cloneDeep(context.value.metrics);
    updatedMetrics[index].filterCondition = !isEmpty(conditions?.operands)
      ? conditions
      : null;
    context.updateContext({ ...context.value, metrics: updatedMetrics });
  };

  const handleDeletion = async () => {
    if (context.value.metrics.length === 1) {
      return;
    }
    const updatedMetrics = cloneDeep(context.value.metrics)
      .filter((met, i) => i !== index)
      ?.map((met, ind) => ({ ...met, metricOrder: ind + 1 }));
    await context.updateContext({ ...context.value, metrics: updatedMetrics });
    validateChartForm();
  };

  const ruleBuilderContext = {
    rule: context.value.metrics[index].filterCondition,
    updateAutomationRule
  };

  const selectAttributeHandler = (val) => {
    setSelectedMetricAttribute(val);
    setSelectedFunction(val.metricFunctions[0]);
    const updatedMetrics = cloneDeep(context.value.metrics);
    updatedMetrics[index].attribute = val.value;
    updatedMetrics[index].metricFunction = val.metricFunctions[0];
    setSelectedUnit(val.defaultMetricUnit);
    updatedMetrics[index].metricUnit = val.defaultMetricUnit;
    if (updatedMetrics[index]?.denominator) {
      // removing denominator from metrics on attribute changes
      const { denominator, ...rest } = updatedMetrics[index];
      updatedMetrics[index] = rest;
    }
    context.updateContext({ ...context.value, metrics: updatedMetrics });
  };

  const selectFunctionHandler = (val) => {
    setSelectedFunction(val);
    const updatedMetrics = cloneDeep(context.value.metrics);
    updatedMetrics[index].metricFunction = val.value;
    if (val.value == 'PERCENTAGE') {
      updatedMetrics[index].denominator = DENOMINATOR_VALUES[0].value;
    } else if (updatedMetrics[index]?.denominator) {
      // removing denominator from metrics on function changes
      const { denominator, ...rest } = updatedMetrics[index];
      updatedMetrics[index] = rest;
    }
    context.updateContext({ ...context.value, metrics: updatedMetrics });
  };

  const selectUnitHandler = (val) => {
    setSelectedUnit(val);
    const updatedMetrics = cloneDeep(context.value.metrics);
    updatedMetrics[index].metricUnit = val.value;
    context.updateContext({ ...context.value, metrics: updatedMetrics });
  };

  const selectDenominatorHandler = (val) => {
    setselectedDenominator(val);
    const updatedDenominator = cloneDeep(context.value?.metrics);
    updatedDenominator[index].denominator = val.value;
    context.updateContext({ ...context.value, metrics: updatedDenominator });
  };

  useEffect(() => {
    const updatedMetrics = cloneDeep(context.value.metrics);
    updatedMetrics[index] = {
      ...updatedMetrics[index],
      name:
        updatedMetrics[index].name ||
        `${getTranslatedText(intl, 'reports.chart.metric.untitled')} - ${index +
          1}`
    };
    context.updateContext({ ...context.value, metrics: updatedMetrics });
    setTimeout(() => {
      setBuilderLoading(false);
    }, 1000);
  }, []);

  useEffect(() => {
    if (selectedDenominator?.value == 'GROUP' && !context?.value.groupBy) {
      setselectedDenominator(DENOMINATOR_VALUES[0]);
      const updatedDenominator = cloneDeep(context.value?.metrics);
      updatedDenominator[index].denominator = DENOMINATOR_VALUES[0].value;
      context.updateContext({ ...context.value, metrics: updatedDenominator });
    }
  }, [context.value.groupBy]);

  return (
    <MetricConfigWrapper key={index}>
      <div className="flex">
        <div className="f11" superid="metricName">
          <TextField
            placeholder={getTranslatedText(
              intl,
              'reports.chart.metric.placeholder'
            )}
            value={data?.name}
            fontSize="fontMedium"
            noborder
            onChange={(val) => handleChange('name', val)}
            error={errors?.name?.[0]}
          />
        </div>
        <div className="flex g10">
          {totalMetrics > 1 && (
            <div className="deleteBtn fx actr" superid="deleteMetric">
              <Button
                onClick={handleDeletion}
                plain
                icon={<SVGIcon name="Delete" />}
              />
            </div>
          )}
          <div className="toggleBtn fx actr" superid="toggleMetric">
            <Button
              onClick={handleToggle}
              plain
              icon={
                active ? (
                  <SVGIcon name="ArrowDown" />
                ) : (
                  <SVGIcon name="ArrowUp" />
                )
              }
            />
          </div>
        </div>
      </div>
      <Collapsible
        open={active}
        transition={{ duration: '150ms', timingFunction: 'ease' }}
      >
        <div className="mt10 mb10 uppercase">
          <DisplayText>
            <TextStyle variation="subdued" weight="Bold">
              <FormattedMessage id="reports.chart.config.metricDefinition.header" />
            </TextStyle>
          </DisplayText>
        </div>
        <div className="flex mb-20 g10 configCard white-to-grey-bg">
          <div className="w50">
            <FormattedMessage id="reports.chart.config.attribute" />
            <div superid="selectAttribute">
              <Select
                value={selectedMetricAttribute}
                label={getTranslatedText(
                  intl,
                  'reports.chart.config.attribute.label'
                )}
                isLoading={metaLoading}
                isDisabled={metaLoading}
                options={metricAttributes}
                onChange={selectAttributeHandler}
                menuPortalTarget={document.body}
                components={{ IndicatorSeparator, DropdownArrow }}
                classNamePrefix="value_selector"
                formatGroupLabel={formatGroupLabel}
                menuPlacement="auto"
                menuPortalTarget={document.body}
                placeholder={getTranslatedText(
                  intl,
                  'reports.chart.config.attribute.placeholder'
                )}
                closeMenuOnScroll={(e) => {
                  return (
                    e.target &&
                    e.target.classList &&
                    !(e.target.classList.value.indexOf('menu-list') > -1)
                  );
                }}
                styles={
                  errors?.attribute
                    ? {
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999
                        }),
                        ...reactSelectErrorStyles
                      }
                    : {
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999
                        })
                      }
                }
              />
              {errors?.attribute &&
                reactSelectInlineError(errors?.attribute?.[0])}
            </div>
          </div>
          <div className="w50">
            <div className="fx">
              <div className="fx actr mr8">
                <SVGIcon name="Function" size="sm" />
              </div>
              <FormattedMessage id="reports.chart.config.function" />
            </div>
            <div className="flex actr g10">
              <div className="full-width" superid="metricFunction">
                <Select
                  value={selectedFunction}
                  label={getTranslatedText(
                    intl,
                    'reports.chart.config.function.label'
                  )}
                  options={selectedMetricAttribute?.metricFunctions?.map(
                    (func) => ({ value: func, label: func })
                  )}
                  isLoading={metaLoading}
                  isDisabled={metaLoading}
                  menuPortalTarget={document.body}
                  onChange={selectFunctionHandler}
                  components={{ IndicatorSeparator, DropdownArrow }}
                  classNamePrefix="value_selector"
                  formatGroupLabel={formatGroupLabel}
                  menuPlacement="auto"
                  menuPortalTarget={document.body}
                  placeholder={getTranslatedText(
                    intl,
                    'reports.chart.config.function.placeholder'
                  )}
                  closeMenuOnScroll={(e) => {
                    return (
                      e.target &&
                      e.target.classList &&
                      !(e.target.classList.value.indexOf('menu-list') > -1)
                    );
                  }}
                  styles={
                    errors?.metricFunction
                      ? {
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999
                          }),
                          ...reactSelectErrorStyles
                        }
                      : {
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999
                          })
                        }
                  }
                />
                {errors?.metricFunction &&
                  reactSelectInlineError(errors?.metricFunction?.[0])}
              </div>
            </div>
          </div>
          {selectedFunction?.value == 'PERCENTAGE' && (
            <div className="w50">
              <div className="fx">
                <FormattedMessage id="reports.chart.config.denominator" />
                <div className="ml-10">
                  <SuperPop
                    position="top-start"
                    header={
                      <div className="fx g5">
                        <DisplayText>
                          <TextStyle variation="subdued">
                            {getTranslatedText(
                              intl,
                              'reports.widget.config.denominator.tips.header'
                            )}
                          </TextStyle>
                        </DisplayText>
                      </div>
                    }
                    holder={
                      <div className="h-20">
                        <SVGIcon name="About" size="md" />
                      </div>
                    }
                    content={
                      <>
                        <div>
                          <DisplayText weight="Medium">
                            {getTranslatedText(
                              intl,
                              'reports.widget.config.denominator.group.label'
                            )}
                          </DisplayText>
                          <div className="mt4">
                            <DisplayText>
                              <TextStyle variation="subdued">
                                {getTranslatedText(
                                  intl,
                                  'reports.widget.config.denominator.group.infoPoints',
                                  {
                                    ul: (chunks) => <ul>{chunks}</ul>,
                                    li: (chunks) => <li>{chunks}</li>,
                                    b: (chunks) => <b>{chunks}</b>
                                  }
                                )}
                              </TextStyle>
                            </DisplayText>
                          </div>
                        </div>
                        <div className="mt8">
                          <DisplayText weight="Medium">
                            {getTranslatedText(
                              intl,
                              'reports.widget.config.denominator.filter.label'
                            )}
                          </DisplayText>
                          <div className="mt4">
                            <DisplayText>
                              <TextStyle variation="subdued">
                                {getTranslatedText(
                                  intl,
                                  'reports.widget.config.denominator.filter.infoPoints',
                                  {
                                    ul: (chunks) => <ul>{chunks}</ul>,
                                    li: (chunks) => <li>{chunks}</li>,
                                    b: (chunks) => <b>{chunks}</b>
                                  }
                                )}
                              </TextStyle>
                            </DisplayText>
                          </div>
                        </div>
                      </>
                    }
                  />
                </div>
              </div>
              <div className="flex actr g10">
                <div
                  className={`full-width ${!context.value?.groupBy &&
                    'opacity6 cursor-not-allowed'}`}
                  superid="metricFunction"
                >
                  <Select
                    value={selectedDenominator}
                    label={getTranslatedText(
                      intl,
                      'admin.slaMetric.chooseValue.pl'
                    )}
                    options={DENOMINATOR_VALUES}
                    isOptionDisabled={(option) => {
                      if (option.checkDisabledCondition == 'groupBy') {
                        return context.value?.groupBy ? false : true;
                      } else {
                        return false;
                      }
                    }}
                    isLoading={metaLoading}
                    isDisabled={
                      metaLoading ||
                      // !context.value?.metrics[index]?.filterCondition &&
                      !context.value?.groupBy
                    }
                    onChange={selectDenominatorHandler}
                    menuPortalTarget={document.body}
                    components={{ IndicatorSeparator, DropdownArrow }}
                    classNamePrefix="value_selector"
                    formatGroupLabel={formatGroupLabel}
                    menuPlacement="auto"
                    placeholder={getTranslatedText(
                      intl,
                      'admin.slaMetric.chooseValue.pl'
                    )}
                    closeMenuOnScroll={(e) => {
                      return (
                        e.target &&
                        e.target.classList &&
                        !(e.target.classList.value.indexOf('menu-list') > -1)
                      );
                    }}
                    styles={
                      errors?.metricUnits
                        ? {
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999
                            }),
                            ...reactSelectErrorStyles
                          }
                        : {
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999
                            })
                          }
                    }
                  />
                  {errors?.metricUnits &&
                    reactSelectInlineError(errors?.metricUnits?.[0])}
                </div>
              </div>
            </div>
          )}
          {selectedMetricAttribute?.metricUnits && (
            <div className="w50">
              <div className="fx">
                <FormattedMessage id="reports.widget.config.displayUnit" />
              </div>
              <div className="flex actr g10">
                <div className="full-width" superid="metricFunction">
                  <Select
                    value={selectedUnit}
                    label={getTranslatedText(
                      intl,
                      'reports.widget.config.displayUnit'
                    )}
                    options={selectedMetricAttribute?.metricUnits?.map(
                      (func) => ({ value: func, label: func })
                    )}
                    isLoading={metaLoading}
                    isDisabled={metaLoading}
                    onChange={selectUnitHandler}
                    menuPortalTarget={document.body}
                    components={{ IndicatorSeparator, DropdownArrow }}
                    classNamePrefix="value_selector"
                    formatGroupLabel={formatGroupLabel}
                    menuPlacement="auto"
                    menuPortalTarget={document.body}
                    placeholder={getTranslatedText(
                      intl,
                      'reports.widget.config.displayUnit.placeholder'
                    )}
                    closeMenuOnScroll={(e) => {
                      return (
                        e.target &&
                        e.target.classList &&
                        !(e.target.classList.value.indexOf('menu-list') > -1)
                      );
                    }}
                    styles={
                      errors?.metricUnits
                        ? {
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999
                            }),
                            ...reactSelectErrorStyles
                          }
                        : {
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999
                            })
                          }
                    }
                  />
                  {errors?.metricUnits &&
                    reactSelectInlineError(errors?.metricUnits?.[0])}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="mt10 mb10 uppercase ">
          <DisplayText>
            <TextStyle variation="subdued" weight="Bold">
              <FormattedMessage id="reports.chart.config.filterConditions.header" />
            </TextStyle>
          </DisplayText>
        </div>
        {metaLoading || builderLoading ? (
          <div className="conditionCard configCard white-to-grey-bg flex-center">
            <Loader />
          </div>
        ) : (
          <div className="configCard white-to-grey-bg">
            <ReportQueryBuilderForm
              currentModule={MODULEMAP[context.value.entity]}
              meta={meta}
              allEntities={{ getEntitiesMeta: allEntities || [] }}
              allOperators={allOperators}
              context={ruleBuilderContext}
              query={ruleBuilderContext.rule}
              errors={errors?.['filterCondition.operands']}
            />
          </div>
        )}
      </Collapsible>
    </MetricConfigWrapper>
  );
}

export default MetricConfig;
